import React from 'react'
import { Box, Grid, TextField, Typography, Link, Card, SvgIcon } from "@mui/material"
import { styled } from "@mui/material/styles"
import PersonIcon from '@mui/icons-material/Person'
import AppleIcon from '@mui/icons-material/Apple'
import AudiotrackIcon from '@mui/icons-material/Audiotrack'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'

// Spotify Icon SVG Component
const SpotifyIcon = (props) => (
    <SvgIcon {...props} viewBox="0 0 24 24">
        <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z" fill="currentColor"/>
    </SvgIcon>
)

const CssTextField = styled(TextField)(({ theme }) => ({
    fontFamily: "'DM Sans', sans-serif",
    '& .MuiOutlinedInput-root': {
        background: '#ffffff',
        borderRadius: '8px',
        transition: 'all 0.3s ease',
        '& fieldset': {
            borderColor: 'rgba(0, 0, 0, 0.12)',
        },
        '&:hover fieldset': {
            borderColor: 'rgba(26, 115, 232, 0.3)',
        },
        '&.Mui-focused fieldset': {
            borderColor: '#1a73e8',
            borderWidth: '2px',
        },
    },
    '& .MuiOutlinedInput-input': {
        color: '#202124',
        fontFamily: "'DM Sans', sans-serif",
    },
    '& label': {
        color: '#5f6368',
        fontFamily: "'DM Sans', sans-serif",
    },
    '& label.Mui-focused': {
        color: '#1a73e8',
    },
}));

const StyledInputLabel = styled(Typography)({
    fontWeight: 600,
    fontSize: '0.9rem',
    color: '#202124',
    marginBottom: '8px',
    fontFamily: "'DM Sans', sans-serif",
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
});

const InfoCard = styled(Card)({
    background: 'linear-gradient(135deg, rgba(26, 115, 232, 0.08) 0%, rgba(0, 191, 165, 0.08) 100%)',
    borderRadius: '12px',
    padding: '16px',
    border: '1px solid rgba(26, 115, 232, 0.2)',
    marginTop: '16px',
});

function AddArtist(props) {
    const { type, onChange, addArtist } = props;
    const isPrimary = type === "primary";
    const artistTypeLabel = isPrimary ? "Primary" : "Featuring";

    return (
        <Grid container spacing={2.5} className="add-song-details-container" sx={{ pt: 3 }}>
            <Grid item xs={12}>
                <StyledInputLabel>
                    <PersonIcon sx={{ fontSize: '1.2rem', color: isPrimary ? '#1a73e8' : '#00bfa5' }} />
                    Artist Name <span style={{ color: 'red' }}>*</span>
                </StyledInputLabel>
                <CssTextField
                    name="artist_name"
                    placeholder={`Enter ${artistTypeLabel.toLowerCase()} artist name`}
                    variant="outlined"
                    size="small"
                    onChange={onChange}
                    value={addArtist.artist_name}
                    fullWidth
                    required
                />
            </Grid>

            <Grid item xs={12}>
                <StyledInputLabel>
                    <AppleIcon sx={{ fontSize: '1.2rem', color: '#000000' }} />
                    Apple Music Artist ID
                </StyledInputLabel>
                <CssTextField
                    name="apple_id"
                    placeholder="e.g., 1234567890"
                    variant="outlined"
                    size="small"
                    onChange={onChange}
                    value={addArtist.apple_id}
                    fullWidth
                />
                <Typography variant="caption" sx={{ color: '#5f6368', mt: 0.5, display: 'block' }}>
                    Optional - Links your artist profile on Apple Music
                </Typography>
            </Grid>

            <Grid item xs={12}>
                <StyledInputLabel>
                    <SpotifyIcon sx={{ fontSize: '1.2rem', color: '#1DB954' }} />
                    Spotify Artist ID
                </StyledInputLabel>
                <CssTextField
                    name="spotify_id"
                    placeholder="e.g., 3TVXtAsR1Inumwj472S9r4"
                    variant="outlined"
                    size="small"
                    onChange={onChange}
                    value={addArtist.spotify_id}
                    fullWidth
                />
                <Typography variant="caption" sx={{ color: '#5f6368', mt: 0.5, display: 'block' }}>
                    Optional - Links your artist profile on Spotify
                </Typography>
            </Grid>

            <Grid item xs={12}>
                <InfoCard elevation={0}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <InfoOutlinedIcon sx={{ color: '#1a73e8', fontSize: '1.2rem', mt: 0.2 }} />
                        <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#202124', mb: 0.5 }}>
                                Need help finding your Artist IDs?
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#5f6368', display: 'block', mb: 1 }}>
                                Artist IDs help link your release to your existing profiles on streaming platforms.
                            </Typography>
                            <Link
                                href="https://fronicmedia.com/knowledge-base/how-to-find-apple-spotify-artist-id/"
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                    color: '#1a73e8',
                                    fontWeight: 600,
                                    fontSize: '0.875rem',
                                    textDecoration: 'none',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    '&:hover': {
                                        textDecoration: 'underline',
                                    }
                                }}
                            >
                                Learn how to find your Artist IDs →
                            </Link>
                        </Box>
                    </Box>
                </InfoCard>
            </Grid>
        </Grid>
    )
}

export default AddArtist
